import * as Sentry from '@sentry/react';
import { NEWS } from '@utils/constants';
import { NewsItem, NewsResponse } from '../types/news';

const CACHE_KEY = 'plick_blog_news_cache';
const CACHE_DURATION = 4 * 60 * 60 * 1000; // 4 hours

export const fetchNews = async (): Promise<NewsItem[]> => {
  try {
    // Check cache first
    if (typeof window !== 'undefined') {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        try {
          const { timestamp, data } = JSON.parse(cached);
          if (Date.now() - timestamp < CACHE_DURATION) {
            return data;
          }
        } catch {
          // If parsing fails, proceed to fetch
        }
      }
    }

    const response = await fetch(NEWS.API_URL);
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    const data: NewsResponse = await response.json();

    const cleanedItems = data.items.map((item) => {
      let cleanCategory;
      if (typeof item.category === 'string') {
        try {
          // Intentamos parsear si es un string que contiene un array JSON
          cleanCategory = JSON.parse(item.category);
        } catch {
          // Si falla el parse, usamos el string tal cual
          cleanCategory = item.category;
        }
      } else {
        cleanCategory = item.category;
      }

      // Aseguramos que sea array, descartamos valores no-string (null incluido) y limpiamos cada elemento
      const categoryArray = Array.isArray(cleanCategory) ? cleanCategory : [cleanCategory];
      const finalCategory = categoryArray
        .filter((cat): cat is string => typeof cat === 'string')
        .map((cat) => cat.trim());

      return {
        ...item,
        category: finalCategory,
      };
    });

    const sortedItems = cleanedItems.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    // Update cache
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(
          CACHE_KEY,
          JSON.stringify({
            timestamp: Date.now(),
            data: sortedItems,
          }),
        );
      } catch {
        // Ignore storage errors
      }
    }

    return sortedItems;
  } catch (error) {
    Sentry.captureException(error);
    return [];
  }
};
